import React, { useState } from "react";

import { Alert, Box, CircularProgress, Grid, TextField } from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { OrderBetween } from "@safetyculture/s12-apis-connect-web/s12/accounts/admin/v1/white_label_pb";

import { ReplacementRow } from "./custom-labels-table-components";
import { CreateWhiteLabelReplacement } from "api/endpoints/custom-labels";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { GrpcError } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

export interface CustomerLabelCreateProps {
  onCancel: (event: object, reason: string) => void;
  onComplete: () => void;
  replacements: ReplacementRow[];
}

const CustomerLabelCreate = ({
  onCancel,
  onComplete,
  replacements,
}: CustomerLabelCreateProps) => {
  const classes = mainStyles();
  useParams<UrlParamTypes>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<GrpcError>();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [searchValue, setSearchValue] = useState("");
  const [replaceValue, setReplaceValue] = useState("");

  // Create will append to the end of the list with the largest orderKey
  const createReplacement = async () => {
    try {
      setIsProcessing(true);

      const orderBetween = new OrderBetween({
        prevKey: replacements[replacements.length - 1]?.orderKey || "", // Attemp to get the prevKey if any, otherwise default to empty string
        nextKey: "", // Leave nextKey empty to assign the largest orderKey
      });

      await CreateWhiteLabelReplacement(
        roleId,
        searchValue,
        replaceValue,
        orderBetween,
        callOptionsHeaders(regionCode, roleId),
      );
      onComplete();
    } catch (e) {
      setError(e as GrpcError);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderErrorMessage = () => {
    if (!error) {
      return;
    }
    return (
      <Alert severity="error" variant="filled" data-test-id="error-message">
        {error?.message}
      </Alert>
    );
  };

  return (
    <>
      <ConfirmDialog
        title="Create Customer Label"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={true}
        setOpen={() => {}}
        onConfirm={() => {
          setIsProcessing(true);
          createReplacement();
        }}
        onCancel={() => onCancel({}, "cancel")}
        disabled={isProcessing}
        disableCancelBtn={isProcessing}
      >
        {isProcessing ? (
          <Grid
            container
            justifyContent="center"
            className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
          >
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
              data-testid="loading-icon"
            />
          </Grid>
        ) : (
          <>
            <Box
              sx={{
                ...classes.spaceTopSmall,
                width: "100%",
                paddingTop: "0.5rem",
              }}
            >
              <TextField
                inputProps={{
                  "data-testid": "enter-custom-label-search-value-field",
                }}
                fullWidth
                margin="dense"
                label="Enter search value"
                variant={"outlined"}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                disabled={isProcessing}
              />
              <TextField
                inputProps={{
                  "data-testid": "enter-custom-label-replace-value-field",
                }}
                fullWidth
                margin="dense"
                label="Enter replace value"
                variant={"outlined"}
                value={replaceValue}
                onChange={(e) => setReplaceValue(e.target.value)}
                disabled={isProcessing}
              />
            </Box>
            <Box
              sx={{ width: "100%", paddingTop: "0.5rem" }}
              className={clsx(classes.spaceTopSmall)}
            >
              {renderErrorMessage()}
            </Box>
          </>
        )}
      </ConfirmDialog>
    </>
  );
};

export default CustomerLabelCreate;
